/**
 * FeatureIdeaCardView (T35.7.1 / T35.7.2 Part A) — the ONE interactive feature-idea card.
 *
 * The real version of the prototype's `.prop` card. Rendered in BOTH the
 * Feedback-tab published list (`FeatureIdeasSection`) AND the propose sheet's
 * similar-search results (`ProposeFeatureSheet`) — a match is the SAME full card,
 * not a bare title+upvote row (proto's `proposeCard`, "fully functional
 * (vote → why → back) here").
 *
 * Shows the idea title, description, and "From N similar reports" provenance,
 * with a mutually-exclusive 👍 / 👎 vote (the upvote count rises by one only while
 * liked; re-tapping the same direction clears it). Casting a vote reveals the
 * "why" field — its prompt swaps with the direction — saved via the /why endpoint
 * (onto the vote row) to "✓ Saved". An UPVOTE also reveals the backing offer (the
 * T35.7.2 gated stub, wired through `BackIdeaDialog` → `SubscriptionPicker` →
 * `/api/subscriptions/:id/subscribe`).
 *
 * Standalone feature (ADR-012): the vote / why / search / propose calls are owned
 * here (`./api`); the donation bits compose from `../subscriptions/` (T35.7.2).
 * No shared feedback-corpus dependency.
 *
 * Token-only (CP5); mobile-first; ≥44px targets.
 */

import { useState, type CSSProperties } from 'react';
import { voteFeatureIdea, saveFeatureIdeaWhy, type FeatureIdeaCard, type VoteDirection } from './api';
import { BackIdeaDialog } from './BackIdeaDialog';

export const cardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
  padding: 'var(--space-4)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-lg)',
  backgroundColor: 'var(--color-bg-raised)',
  boxShadow: 'var(--shadow-sm)',
};

const cardTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-base)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
};

const cardBodyStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.45,
};

const cardProvStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2)',
};

function reactBtnStyle(on: boolean, kind: VoteDirection): CSSProperties {
  const base: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--space-1)',
    minHeight: '2.75rem',
    borderWidth: '0.0625rem',
    borderStyle: 'solid',
    borderColor: 'var(--color-border-strong)',
    background: 'var(--color-bg-primary)',
    borderRadius: 'var(--radius-pill)',
    padding: 'var(--space-2) var(--space-3)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };
  if (!on) return base;
  return kind === 'up'
    ? { ...base, borderColor: 'var(--color-accent-primary)', background: 'var(--color-accent-subtle)', color: 'var(--color-accent-primary)' }
    : { ...base, borderColor: 'var(--color-text-muted)', background: 'var(--color-bg-muted)', color: 'var(--color-text-secondary)' };
}

const revealStyle: CSSProperties = {
  marginTop: 'var(--space-2)',
  paddingTop: 'var(--space-3)',
  borderTopWidth: '0.0625rem',
  borderTopStyle: 'solid',
  borderTopColor: 'var(--color-border-subtle)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
};

const whyQStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
};

const whyTextareaStyle: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  font: 'inherit',
  fontSize: 'var(--font-size-sm)',
  padding: 'var(--space-2) var(--space-3)',
  border: '0.0625rem solid var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--color-text-primary)',
  backgroundColor: 'var(--color-bg-primary)',
  resize: 'vertical',
  minHeight: '3.5rem',
};

const whyRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-2)',
};

const whyPrivStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
};

const whySaveStyle: CSSProperties = {
  minHeight: '2.75rem',
  border: 0,
  background: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  borderRadius: 'var(--radius-md)',
  padding: '0 var(--space-4)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

// The post-upvote backing offer (the proto's `.donate-offer`).
const donateOfferStyle: CSSProperties = {
  marginTop: 'var(--space-2)',
  padding: 'var(--space-3)',
  border: '0.0625rem solid var(--color-accent-secondary)',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-accent-subtle)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-1)',
};

const doHeadStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-accent-primary)',
};

const doDescStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.45,
};

const backOpenStyle: CSSProperties = {
  alignSelf: 'flex-start',
  marginTop: 'var(--space-1)',
  minHeight: '2.75rem',
  border: 0,
  background: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  borderRadius: 'var(--radius-pill)',
  padding: '0 var(--space-4)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const WHY_PROMPT: Record<VoteDirection, string> = {
  up: 'What would make this great?',
  down: "What's off about it? A few words help.",
};

/**
 * One feature-idea card with its vote + the post-vote "why" reveal + the
 * post-upvote backing offer. `idea` carries the full card payload; `onVoted`
 * lifts the vote result so the caller (list or sheet) can update its row.
 */
export function FeatureIdeaCardView({
  idea,
  onVoted,
}: {
  idea: FeatureIdeaCard;
  onVoted: (id: string, result: { my_vote: VoteDirection | null; upvotes: number }) => void;
}): JSX.Element {
  const [why, setWhy] = useState('');
  const [savedWhy, setSavedWhy] = useState(false);
  const [busy, setBusy] = useState(false);
  // Backing state for THIS idea (T35.7.2). The BackIdeaDialog owns the one-and-only
  // success message (the universal thank-you); on success the dialog just closes —
  // there is NO second "you back $X" message on the card (anti-drift, T35.7.2.2).
  const [backOpen, setBackOpen] = useState(false);
  const [backBusy, setBackBusy] = useState(false);

  async function vote(direction: VoteDirection): Promise<void> {
    if (busy) return;
    setBusy(true);
    try {
      const result = await voteFeatureIdea(idea.id, direction);
      onVoted(idea.id, result);
    } catch {
      /* leave state; a re-tap retries */
    } finally {
      setBusy(false);
    }
  }

  async function save(): Promise<void> {
    try {
      await saveFeatureIdeaWhy(idea.id, why);
      setSavedWhy(true);
    } catch {
      setSavedWhy(false);
    }
  }

  return (
    <div style={cardStyle} data-testid={`feature-idea-${idea.id}`} data-vote={idea.my_vote ?? ''}>
      <h3 style={cardTitleStyle}>{idea.title}</h3>
      {idea.description && <p style={cardBodyStyle}>{idea.description}</p>}
      {idea.from > 0 && (
        <p style={cardProvStyle}>
          From {idea.from} similar report{idea.from === 1 ? '' : 's'}
        </p>
      )}
      <div style={actionsStyle}>
        <button
          type="button"
          style={reactBtnStyle(idea.my_vote === 'up', 'up')}
          aria-pressed={idea.my_vote === 'up'}
          aria-label="Upvote this idea"
          disabled={busy}
          data-testid={`feature-idea-upvote-${idea.id}`}
          onClick={() => vote('up')}
        >
          <span aria-hidden="true">👍</span>
          <span data-testid={`feature-idea-upvotes-${idea.id}`}>{idea.upvotes}</span>
        </button>
        <button
          type="button"
          style={reactBtnStyle(idea.my_vote === 'down', 'down')}
          aria-pressed={idea.my_vote === 'down'}
          aria-label="Not for me"
          disabled={busy}
          data-testid={`feature-idea-downvote-${idea.id}`}
          onClick={() => vote('down')}
        >
          <span aria-hidden="true">👎</span>
        </button>
      </div>

      {idea.my_vote != null && (
        <div style={revealStyle} data-testid={`feature-idea-reveal-${idea.id}`}>
          <span style={whyQStyle}>{WHY_PROMPT[idea.my_vote as VoteDirection]}</span>
          <textarea
            style={whyTextareaStyle}
            aria-label="Your note, private to the team"
            placeholder="Explain in a few words…"
            value={why}
            onChange={(e) => {
              setWhy(e.target.value);
              setSavedWhy(false);
            }}
            data-testid={`feature-idea-why-${idea.id}`}
          />
          <div style={whyRowStyle}>
            <span style={whyPrivStyle}>
              <svg
                viewBox="0 0 24 24"
                width="0.8125rem"
                height="0.8125rem"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
                style={{ verticalAlign: '-0.1rem', marginRight: 'var(--space-1)' }}
              >
                <rect x="5" y="11" width="14" height="9" rx="2" />
                <path d="M8 11V8a4 4 0 018 0v3" />
              </svg>
              Private — only the team sees this
            </span>
            <button
              type="button"
              style={whySaveStyle}
              onClick={save}
              disabled={why.trim() === ''}
              data-testid={`feature-idea-why-save-${idea.id}`}
            >
              {savedWhy ? '✓ Saved' : 'Save'}
            </button>
          </div>

          {/* The backing offer follows an UPVOTE only — hidden on dislike (AC2).
              You don't fund what you dislike. Bracketed-influence copy, verbatim. */}
          {idea.my_vote === 'up' && (
            <div style={donateOfferStyle} data-testid={`feature-idea-donate-${idea.id}`}>
              <span style={doHeadStyle}>
                <span aria-hidden="true">💜</span> Give your vote more weight
              </span>
              <p style={doDescStyle}>Chip in a dollar or two — it shows how much this one matters.</p>
              <button
                type="button"
                style={backOpenStyle}
                onClick={() => setBackOpen(true)}
                data-testid={`feature-idea-back-open-${idea.id}`}
              >
                Back this ›
              </button>
            </div>
          )}
        </div>
      )}

      <BackIdeaDialog
        open={backOpen}
        ideaId={idea.id}
        ideaTitle={idea.title}
        busy={backBusy}
        setBusy={setBackBusy}
        onClose={() => setBackOpen(false)}
        onConfirmed={() => {
          // The dialog owns the one-and-only success message; nothing to show on
          // the card. It stays open on its thank-you until the user taps Done,
          // which calls onClose. No second "you back $X" message (anti-drift).
        }}
      />
    </div>
  );
}
